import React, { useState } from "react";
import { Backdrop, Content, Inner } from "./Modal.styles";
import Portal from "../Portal";

export interface Props {
  open: boolean;
  children?: React.ReactNode;
  onClose?: () => void;
  maxWidth?: number;
  width?: string;
  disableScroll?: boolean;
  style?: React.CSSProperties;
}

export default function Modal({
  open,
  children,
  onClose,
  maxWidth,
  width = "100%",
  disableScroll = false,
  style,
}: Props) {
  const [active, setActive] = useState(false);
  const backdrop = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const { current } = backdrop;

    const transitionEnd = () => setActive(open);

    const keyHandler = (e: KeyboardEvent) =>
      [27].indexOf(e.which) >= 0 && onClose && onClose();

    const clickHandler = (e: MouseEvent) =>
      e.target === current && onClose && onClose();

    if (current) {
      current.addEventListener("transitionend", transitionEnd);
      current.addEventListener("click", clickHandler);
      window.addEventListener("keyup", keyHandler);
    }

    if (open) {
      window.setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).activeElement.blur();
        setActive(open);
      }, 10);
    }

    return () => {
      if (current) {
        current.removeEventListener("transitionend", transitionEnd);
        current.removeEventListener("click", clickHandler);
      }

      window.removeEventListener("keyup", keyHandler);
    };
  }, [open, onClose]);

  return (
    <React.Fragment>
      {(open || active) && (
        <Portal className="modal-portal">
          <Backdrop ref={backdrop} className={active && open ? "active" : ""}>
            <Content
              className="modal-content"
              $maxWidth={maxWidth ?? 800}
              $width={width}
              $disableScroll={disableScroll}
            >
              <Inner style={style}>{children}</Inner>
            </Content>
          </Backdrop>
        </Portal>
      )}
    </React.Fragment>
  );
}
